import { EMPTY_OBJECT, EMPTY_STRING } from '@e-socna/empty';
import { isDefined } from '@e-socna/utils';
import { betterFetch, betterFetchFactory } from '@e-socna/better-fetch';
import type { BetterFetchLogger } from '@e-socna/better-fetch';

import { GEOPL_API_PROXY_ENDPOINT } from '../proxy/constants/endpoints.constant';
import type { LatLonCoordinates } from '../types/lat-lon-coordinates.type';

import { createGeoplFetcher } from './fetcher';
import { NAVIGATION_ITINERARY_API_ENDPOINT } from './constants/endpoints.constant';
import type { Itinerary } from './types/itinerary.type';

interface Params {
  readonly resource?: string;
  readonly startPosition: LatLonCoordinates | null | undefined;
  readonly endPosition: LatLonCoordinates | null | undefined;
  readonly intermediates?: readonly LatLonCoordinates[];
  readonly profile?: 'car' | 'pedestrian';
  readonly optimization?: 'fastest' | 'shortest';
  readonly geometryFormat?: 'geojson' | 'polyline';
  readonly constraints?: object;
  readonly getSteps?: boolean;
  readonly getBbox?: boolean;
  readonly distanceUnit?: 'meter' | 'kilometer';
  readonly timeUnit?: 'second' | 'minute' | 'hour';
  readonly crs?: string;
  readonly waysAttributes?: readonly string[];
}

export type { Params as NavigationParams };

const DEFAULT_RESOURCE = 'bdtopo-osrm';
const DEFAULT_PROFILE = 'car';
const DEFAULT_OPTIMIZATION = 'fastest';
const DEFAULT_DISTANCE_UNIT = 'kilometer';
const DEFAULT_TIME_UNIT = 'hour';
const DEFAULT_CRS = 'EPSG:4326';
const DEFAULT_GET_STEPS = 'true';
const DEFAULT_GET_BBOX = 'true';

export const navigationFetcher = (
  pParams: Params,
  backendUrl: string,
  logger?: BetterFetchLogger,
) => {
  const params = {
    resource: pParams.resource ?? DEFAULT_RESOURCE,
    start: `${pParams.startPosition?.lon.toFixed(6) ?? EMPTY_STRING},${pParams.startPosition?.lat.toFixed(6) ?? EMPTY_STRING}`,
    end: `${pParams.endPosition?.lon.toFixed(6) ?? EMPTY_STRING},${pParams.endPosition?.lat.toFixed(6) ?? EMPTY_STRING}`,
    ...(pParams.intermediates
      ? {
          intermediates: pParams.intermediates
            .map(
              (intermediate) =>
                `${intermediate.lon.toFixed(6)},${intermediate.lat.toFixed(6)}`,
            )
            .join(';'),
        }
      : EMPTY_OBJECT),
    profile: pParams.profile ?? DEFAULT_PROFILE,
    optimization: pParams.optimization ?? DEFAULT_OPTIMIZATION,
    ...(pParams.geometryFormat
      ? { geometryFormat: pParams.geometryFormat }
      : EMPTY_OBJECT),
    ...(pParams.constraints ?? EMPTY_OBJECT),
    getSteps: !isDefined(pParams.getSteps)
      ? DEFAULT_GET_STEPS
      : String(pParams.getSteps),
    getBbox: !isDefined(pParams.getBbox)
      ? DEFAULT_GET_BBOX
      : String(pParams.getBbox),
    distanceUnit: pParams.distanceUnit ?? DEFAULT_DISTANCE_UNIT,
    timeUnit: pParams.timeUnit ?? DEFAULT_TIME_UNIT,
    crs: pParams.crs ?? DEFAULT_CRS,
    ...(pParams.waysAttributes
      ? { waysAttributes: pParams.waysAttributes.join(',') }
      : EMPTY_OBJECT),
  };

  const queryString = new URLSearchParams(params).toString();

  const url = `${backendUrl}${GEOPL_API_PROXY_ENDPOINT}/${NAVIGATION_ITINERARY_API_ENDPOINT}?${queryString}`;

  const fetch = logger ? betterFetchFactory(logger) : betterFetch;
  const geoplFetch = createGeoplFetcher('routing', fetch);

  return {
    url,
    fetcher: () => geoplFetch<Itinerary>(url),
  };
};
